<template>
	<div>
		<ValidationObserver
			v-show="!forgotPassword"
			ref="loginForm"
			v-slot="{ invalid, errors }"
			class="sign-in"
			tag="form"
			@submit.prevent="onLogin"
			novalidate
		>
			<h2 v-html="$t('login.loginTitle')" v-if="!hideTitle"/>

			<GeneralMessage
				v-if="loginResponse.message"
				:message="loginResponse.message"
				:type="loginResponse.type"
				@close="loginResponse.message = ''"
			/>

			<!-- EMAIL -->
			<ValidationProvider
				class="input-wrapper"
				name="email"
				rules="required|email"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				ref="loginEmail"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
			>
				<FormInput
					type="email"
					name="email"
					autocomplete="email"
					:placeholder="$t('forms.labels.email')"
					:label="$t('forms.labels.email')"
					v-model="loginForm.email"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<!-- PASSWORD -->
			<ValidationProvider
				name="password"
				rules="required"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				class="input-wrapper"
				:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
			>
				<FormInput
					type="password"
					name="password"
					autocomplete="current-password"
					:placeholder="$t('forms.labels.password')"
					:label="$t('forms.labels.password')"
					v-model="loginForm.password"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<Button
				@click.prevent="toggleForgotPassword(true)"
				type="button"
				level="tertiary"
				:text="$t('login.forgotPasswordButton')"
				:disabled="disabled"
				arrow
			/>

			<Button
				type="submit"
				level="primary"
				:text="$t('login.loginButton')"
				:disabled="disabled"
			/>
		</ValidationObserver>


		<ValidationObserver
			v-show="forgotPassword"
			ref="resetForm"
			v-slot="{ invalid, errors }"
			class="sign-in"
			tag="form"
			@submit.prevent="onResetPassword"
			novalidate
		>
			<h2 v-html="$t('login.forgotPasswordTitle')" v-if="!hideTitle"/>

			<GeneralMessage
				v-if="resetResponse.message"
				:message="resetResponse.message"
				:type="resetResponse.type"
				@close="resetResponse.message = ''"
			/>

			<!-- EMAIL -->
			<ValidationProvider
				class="input-wrapper"
				name="email"
				rules="required|email"
				v-slot="{ errors, invalid, validated, dirty }"
				tag="div"
				ref="loginEmail"
				:customMessages="{ required: $t('forms.errors.fieldEmpty'), email: $t('forms.errors.emailInvalid') }"
			>
				<FormInput
					type="email"
					name="email"
					autocomplete="email"
					:placeholder="$t('forms.labels.email')"
					:label="$t('forms.labels.email')"
					v-model="loginForm.email"
					:error="invalid && validated"
					:disabled="disabled"
					required
				/>
				<span
					class="input-error"
					v-if="invalid && validated"
					v-html="errors[0]"
				/>
			</ValidationProvider>

			<Button
				@click.prevent="toggleForgotPassword(false)"
				type="button"
				level="tertiary"
				:text="$t('login.rememberPasswordButton')"
				:disabled="disabled"
				arrow
			/>

			<Button
				type="submit"
				level="primary"
				:text="$t('login.resetPasswordButton')"
				:disabled="disabled"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import GenericMixin from '@/mixins/GenericVueMixin';
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import Button from '@/components/Button'
import GeneralMessage from '@/components/GeneralMessage'
import sleep from '@/utils/sleep';

export default {
	mixins: [
		GenericMixin,
	],
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		Button,
		GeneralMessage,
	},
	props: {
		hideTitle: Boolean
	},
	data() {
		return {
			forgotPassword: !!this.$root.options.forgotPassword,
			loginForm: {
				email: "",
				password: "",
				userGroup: this.$root.options.userGroup,
				userCoupon: this.$root.options.userCoupon,
			},
			loginResponse: {
				type: 'info',
				message: '',
			},
			resetResponse: {
				type: 'info',
				message: '',
			},
		}
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn"
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
		disabled() {
			return !!this.isLoggedIn || this.loading;
		},
	},
	methods: {
		toggleForgotPassword(state) {
			this.resetForm('login', false);
			this.resetForm('reset', false);

			if (state !== undefined) {
				this.forgotPassword = !!state;
			}
			else {
				this.forgotPassword = !this.forgotPassword;
			}
		},
		async onLogin() {
			const isValid = await this.$refs.loginForm.validate()
			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					const response = await this.$store.dispatch('login', {...this.loginForm})
					if (response.redirect && response.redirect.key) {
						return this.$store.dispatch('redirect', {
							name: response.redirect.key,
							params: response.redirect.params || {},
						})
					}
					this.$emit('loggedIn');
					this.resetForm();
					this.loginResponse.message = this.$t('login.successLogin');
					this.loginResponse.type = 'success';
				} catch(err) {
					try {
            if (err.isMaintenanceError) {
              this.onMaintenanceMode({ callback: () => this.onLogin(), retry: true })
            }	else {
              let message = this.$t('forms.errors.generic')
              const data = err.response.data;

              if (data) {
                if (data.redirect && data.redirect.key) {
                  return this.$store.dispatch('redirect', {
                    name: data.redirect.key,
                    params: data.redirect.params || {},
                  })
                }

                if (data.errors && data.errors.email) {
                  message = data.errors.email[0]
                } else {
                  message = data.message
                }
              }

              this.loginResponse.message = message;
              // TODO: check for status code and change type
              this.loginResponse.type = 'error';
            }
					}
					catch(er) {
						// we don't actually need to do anything here
					}
				}
				this.loading = false;
			}
		},
		async onResetPassword() {
			const isValid = await this.$refs.resetForm.validate()

			if (isValid) {
				this.loading = true;
				document.activeElement.blur();
				try {
					let result = await this.$store.dispatch('account/requestPasswordReset', { email: this.loginForm.email })
          this.resetResponse.message = this.$t('login.successPasswordReset');
				  this.resetResponse.type = 'success';
				}
				catch(err) {
          if (err.isMaintenanceError) {
            this.onMaintenanceMode({ callback: () => this.onResetPassword(), retry: true })
          }	else {
            if (err.response.data.code != 500) {
              this.resetResponse.message =this.$t('forms.errors.generic')
				      this.resetResponse.type = 'error';
            }
          }
				}

				this.resetForm('reset');
				this.loading = false;
			}
		},
		resetForm(form, inputs = true) {
			try {
				if (!form || form === 'login') {
					if (inputs) {
						this.loginForm.email = '';
						this.loginForm.password = '';
					}
					this.loginResponse.message = '';
					this.$refs.loginForm.reset();
				}
				if (!form || form === 'reset') {
					if (inputs) {
						this.loginForm.email = '';
					}
					this.resetResponse.message = '';
					this.$refs.resetForm.reset();
				}
				if (!form) {
					this.loading = false;
					this.forgotPassword = false;
				}
				if (inputs) {
					document.activeElement.blur();
				}
			}
			catch(err) {}
		},
	}
}
</script>

<style>

</style>
